/**
 * The reported-answer picker (Balanced / Fastest / Fewest cleans / Least
 * disruption / Alternative …), in the Scheduler design system.
 *
 * The trigger matches the `PillButton` ghost pills beside it in the top strip
 * (24px, 2-3px radius, teal ink); the menu lays the answers out as a small
 * table, because the options differ only in their numbers and a planner
 * compares them column by column. Figures are mono and tabular so they line
 * up down the list.
 *
 * The list arrives already ordered as a LADDER (see VARIANT_ORDER in
 * SchedulerView.data.ts): the run's own answer first, then finish time rising
 * and disruption falling. This component keeps that order and never re-sorts,
 * but answers showing identical figures are merged into one row named after
 * all of them (`groupIdenticalVariants`), so three labels on one schedule read
 * as one option rather than three that look like a choice.
 * Disruption is shown on every row, since it is the column the later answers
 * exist for; a manifest from an older API has no such figure, and that shows
 * as a dash, never as 0h. Plant clear (API 0.4.14+) is shown beside Finish
 * because "Fastest" breaks a finish-time tie on it: without it, a Fastest
 * carrying more cleans than a same-hour row below it looks like a mistake.
 */
import { memo, useState } from 'react'
import { Box, Menu, MenuItem } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import CheckIcon from '@mui/icons-material/Check'
import {
  groupIdenticalVariants,
  ScheduleVariantKey,
  ScheduleVariantOption,
} from 'Features/SchedulerView/SchedulerView.data'
import { FONT_SANS, RADII, SHADOWS, TOKENS, TYPE } from './SchedulerDesign'

const COLS = 'minmax(150px, 1fr) 64px 72px 52px 78px 16px'

const hours = (v: number | null | undefined) =>
  v === undefined || v === null ? '—' : `${Math.round(v)}h`

export const ScheduleVariantSelect = memo(
  ({
    value,
    options,
    onChange,
    rescheduleMakespan,
  }: {
    value: ScheduleVariantKey
    /** The manifest rows, in ladder order. Empty/absent = a run with no
     *  alternatives, which still shows its own answer as "Balanced". */
    options: ScheduleVariantOption[] | undefined
    onChange: (key: ScheduleVariantKey) => void
    /** Set while a mid-week reschedule is on screen (its production end).
     *  A reschedule reports no alternative answers, and the manifest's list
     *  belongs to the last fresh run — offering it here would put another
     *  schedule's finish times beside this one's grid. So the control shows
     *  the reschedule alone and opens nothing. */
    rescheduleMakespan?: number
  }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const open = Boolean(anchorEl)

    if (rescheduleMakespan !== undefined) {
      return (
        <Box
          component="span"
          title={
            'A mid-week reschedule reports one schedule. Use "Show original" ' +
            'on the schedule to see the original run and its answers.'
          }
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            height: 24,
            px: '9px',
            flexShrink: 0,
            bgcolor: TOKENS.surface0,
            border: `1px solid ${TOKENS.line300}`,
            borderRadius: RADII.md,
            fontFamily: FONT_SANS,
            whiteSpace: 'nowrap',
          }}
        >
          <Box
            component="span"
            sx={{ ...TYPE.micro, fontSize: 10, color: TOKENS.ink400 }}
          >
            Answer
          </Box>
          <Box
            component="span"
            sx={{ fontSize: 11.5, fontWeight: 600, color: TOKENS.brandTeal }}
          >
            Rescheduled
          </Box>
          <Box
            component="span"
            sx={{ ...TYPE.data, fontSize: 11, color: TOKENS.ink500 }}
          >
            {hours(rescheduleMakespan)}
          </Box>
        </Box>
      )
    }
    const list = groupIdenticalVariants(options ?? [])
    const current = list.find((o) => o.keys.includes(value))

    return (
      <>
        <Box
          component="button"
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={(e: React.MouseEvent<HTMLElement>) =>
            setAnchorEl(e.currentTarget)
          }
          title="Which of this run's reported answers the schedule shows"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            height: 24,
            px: '9px',
            flexShrink: 0,
            bgcolor: open ? TOKENS.tealTint : TOKENS.surface0,
            border: `1px solid ${open ? TOKENS.teal300 : TOKENS.line300}`,
            borderRadius: RADII.md,
            cursor: 'pointer',
            fontFamily: FONT_SANS,
            whiteSpace: 'nowrap',
            transition: 'all .12s',
            '&:hover': {
              bgcolor: TOKENS.tealTint,
              borderColor: TOKENS.teal300,
            },
            '&:focus-visible': {
              outline: 'none',
              boxShadow: '0 0 0 3px rgba(0,130,179,.28)',
            },
          }}
        >
          <Box
            component="span"
            sx={{ ...TYPE.micro, fontSize: 10, color: TOKENS.ink400 }}
          >
            Answer
          </Box>
          <Box
            component="span"
            title={current?.label}
            sx={{
              fontSize: 11.5,
              fontWeight: 600,
              color: TOKENS.brandTeal,
              maxWidth: 220,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {current?.label ?? 'Balanced'}
          </Box>
          {current && (
            <Box
              component="span"
              sx={{ ...TYPE.data, fontSize: 11, color: TOKENS.ink500 }}
            >
              {hours(current.makespan)} · {Math.round(current.cleans)} cleans
              {current.disruption_hours != null
                ? ` · ${hours(current.disruption_hours)} disr.`
                : ''}
            </Box>
          )}
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 15,
              color: TOKENS.ink400,
              transition: 'transform .15s',
              transform: open ? 'rotate(180deg)' : 'none',
            }}
          />
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          MenuListProps={{ dense: true, sx: { py: 0 } }}
          PaperProps={{
            sx: {
              mt: '4px',
              minWidth: 480,
              borderRadius: RADII.md,
              border: `1px solid ${TOKENS.line200}`,
              boxShadow: SHADOWS.pop,
              fontFamily: FONT_SANS,
            },
          }}
        >
          {/* Column heads — not focusable, so arrow keys move between answers only. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: COLS,
              gap: '10px',
              px: '12px',
              py: '7px',
              bgcolor: TOKENS.panelHead,
              borderBottom: `1px solid ${TOKENS.line200}`,
              ...TYPE.micro,
              fontSize: 10,
            }}
          >
            <Box>Answer</Box>
            <Box sx={{ textAlign: 'right' }}>Finish</Box>
            <Box
              sx={{ textAlign: 'right' }}
              title="When the last clean or changeover ends and the plant is free. Fastest breaks a tie on finish time by clearing the plant first."
            >
              Plant clear
            </Box>
            <Box sx={{ textAlign: 'right' }}>Cleans</Box>
            <Box sx={{ textAlign: 'right' }}>Disruption</Box>
            <Box />
          </Box>

          {list.length === 0 && (
            <MenuItem
              selected
              onClick={() => setAnchorEl(null)}
              sx={{ ...TYPE.small, px: '12px' }}
            >
              Balanced — this run reported no alternatives
            </MenuItem>
          )}

          {list.map((o) => {
            const selected = o.keys.includes(value)
            return (
              <MenuItem
                key={o.variant_key}
                selected={selected}
                onClick={() => {
                  onChange(o.variant_key)
                  setAnchorEl(null)
                }}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: COLS,
                  gap: '10px',
                  alignItems: 'center',
                  minHeight: 30,
                  px: '12px',
                  py: '4px',
                  borderLeft: `2px solid ${selected ? TOKENS.brandTeal : 'transparent'}`,
                  borderBottom: `1px solid ${TOKENS.line100}`,
                  '&.Mui-selected, &.Mui-selected:hover': {
                    bgcolor: TOKENS.tealTint,
                  },
                  '&:hover': { bgcolor: TOKENS.surface50 },
                }}
              >
                <Box
                  sx={{
                    fontSize: 12,
                    fontWeight: selected ? 700 : 600,
                    color: selected ? TOKENS.brandTeal : TOKENS.ink900,
                    // A merged row names every label it stands for, so it may
                    // need two lines; truncating it would hide which labels agree.
                    whiteSpace: 'normal',
                    lineHeight: 1.3,
                  }}
                >
                  {o.label}
                </Box>
                <Box
                  sx={{
                    ...TYPE.data,
                    textAlign: 'right',
                    color: TOKENS.ink700,
                  }}
                >
                  {hours(o.makespan)}
                </Box>
                <Box
                  sx={{
                    ...TYPE.data,
                    textAlign: 'right',
                    color: TOKENS.ink700,
                  }}
                >
                  {hours(o.plant_clear_hours)}
                </Box>
                <Box
                  sx={{
                    ...TYPE.data,
                    textAlign: 'right',
                    color: TOKENS.ink700,
                  }}
                >
                  {Math.round(o.cleans)}
                </Box>
                <Box
                  sx={{
                    ...TYPE.data,
                    textAlign: 'right',
                    color: TOKENS.ink700,
                  }}
                >
                  {hours(o.disruption_hours)}
                </Box>
                <Box sx={{ display: 'flex', color: TOKENS.brandTeal }}>
                  {selected && <CheckIcon sx={{ fontSize: 14 }} />}
                </Box>
              </MenuItem>
            )
          })}

          <Box
            sx={{
              ...TYPE.small,
              fontSize: 11,
              color: TOKENS.ink400,
              px: '12px',
              py: '7px',
            }}
          >
            Finish time rises down the list and disruption falls — each row is
            the trade against the one above it.
          </Box>
        </Menu>
      </>
    )
  }
)
ScheduleVariantSelect.displayName = 'ScheduleVariantSelect'
